import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Avatar } from '@change-corgi/design-system-legacy/components/media';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { DecisionMaker } from 'src/app/pages/petitionDetails/shared/types';

import type { AllDmResponses } from '../../../../hooks/useDecisionMakerCards';
import { useDecisionMakerCard } from '../../hooks/useDecisionMakerCard';
import { ConstituentCount } from '../ConstituentCount';
import { DmResponseStatus } from '../DmResponseStatus';
import { DMTitleIcon } from '../icons/DMTitleIcon';
import { ReadDmResponse } from '../ReadDmResponse';

type DecisionMakerCardProps = {
	decisionMaker: DecisionMaker;
	allDmResponses: AllDmResponses;
};

export function SunriseDecisionMakerCard({ decisionMaker, allDmResponses }: DecisionMakerCardProps): JSX.Element {
	const photoUrl = decisionMaker.photo?.userMedium.processing ? undefined : decisionMaker.photo?.userMedium.url;
	const {
		actions: { handleDmProfileLinkClick },
	} = useDecisionMakerCard();

	return (
		<Box
			p={24}
			mb={[0, 16]}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				borderRadius: 8,
				boxShadow: '0px 4px 13px 0px rgba(95, 95, 95, 0.07), 0px 18px 26px 0px rgba(123, 123, 123, 0.12)',
				gap: '16px',
			}}
			data-qa="dm-card-sunrise"
		>
			<Flex sx={{ gap: '16px' }}>
				<Flex sx={{ position: 'relative', flexShrink: 0, height: 64 }}>
					<Avatar variant="subtle" data-testid="dm-image" src={photoUrl} size={64} alt="" loading="lazy" />
					{decisionMaker.title && (
						<Box
							data-testid="dm-title-icon"
							sx={{
								position: 'absolute',
								bottom: 0,
								top: '75%',
								left: '50%',
								transform: 'translateX(-50%)',
								width: 48,
								height: 48,
								borderRadius: '50%',
								background: 'sunrise.semantic.fill.accent.primary.weaker',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								borderWidth: '4px',
								borderStyle: 'solid',
								borderColor: 'sunrise.semantic.stroke.inverse.strong',
							}}
						>
							<span sx={{ fill: 'sunrise.semantic.icon.accent.secondary' }}>
								<DMTitleIcon width="24" height="24" />
							</span>
						</Box>
					)}
				</Flex>
				<Flex sx={{ flexDirection: 'column', justifyContent: 'center', gap: '16px' }}>
					<Flex sx={{ flexDirection: 'column', justifyContent: 'center' }}>
						{decisionMaker.publiclyVisible ? (
							<Link
								sx={{ fontWeight: 'bold' }}
								color="primary-black"
								data-testid="dm-link"
								data-qa="dm-link"
								to={`/decision-makers/${decisionMaker.slug || decisionMaker.id}?source_location=petition_details_gamma`}
								onClick={async () => handleDmProfileLinkClick(decisionMaker.id)}
							>
								<Text size="large" fontWeight="semibold" ellipsis>
									{decisionMaker.displayName}
								</Text>
							</Link>
						) : (
							<Text size="large" fontWeight="semibold" ellipsis>
								{decisionMaker.displayName}
							</Text>
						)}

						<Text as="p" size="default" color="sunrise.semantic.typography.strong">
							{decisionMaker.title}
						</Text>
					</Flex>
					<Flex sx={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
						<DmResponseStatus decisionMaker={decisionMaker} allDmResponses={allDmResponses} />
						<ConstituentCount decisionMaker={decisionMaker} />
					</Flex>
				</Flex>
			</Flex>

			<ReadDmResponse decisionMaker={decisionMaker} allDmResponses={allDmResponses} />
		</Box>
	);
}
